import React, { useContext, useEffect, useMemo, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import Select, { components } from 'react-select';
import ReactTooltip from 'react-tooltip';
import { LessonMode } from '../../../Constants';

import ColorPicker from '../tools/ColorPicker';
import Auth from '../../services/AuthService';
import DrawService from '../../services/DrawService';
import PlayerService from '../../services/PlayerService';

import RichTextEditor, { ADVANCED_CKEDITOR_TOOLBAR, ADVANCED_MATH_CKEDITOR_TOOLBAR, MATH_TOOLBAR, MATH_WITH_TABLE_TOOLBAR, MINIMUM_TOOLBAR } from '../RichTextEditor';

import HtmlComponent from '../HtmlComponent';

import '../../../css/Draw.scss';
import useStyleEvents from '../../../hooks/useStyleEvents';
import UtilsService from '../../services/UtilsService';
import lessonService from '../../services/LessonService';
import responseService from '../../services/ResponseService';
import navMenuService from '../../services/NavMenuService';
import { register } from '../../../i18n';
import PrintQuestionNumber from './PrintQuestionNumber';
import ShowPreviousResponseButton from '../ShowPreviousResponseButton';
import SubmissionWarningDialog from '../SubmissionWarningDialog';
import ShowPreviousResponseConfirmDialog from '../ShowPreviousResponseConfirmDialog';
import PreviewUnavailableDialog from '../PreviewUnavailableDialog';

const t = register('GlobalQuestionLabels');
const t2 = register('AriaLabels');

const { ValueContainer, Placeholder, SingleValue } = components;

const CustomValueContainer = ({ children, ...props }) => {
  return (
    <ValueContainer {...props}>
      <Placeholder {...props} isFocused={props.isFocused}>
        {props.selectProps.placeholder}
      </Placeholder>
      {React.Children.map(children, (child) => {
        return child && child.type !== Placeholder && child.type !== SingleValue ? child : null;
      })}
    </ValueContainer>
  );
};

const brushSizeOptions = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
  { value: 6, label: '6' },
  { value: 7, label: '7' },
  { value: 8, label: '8' },
  { value: 9, label: '9' },
  { value: 10, label: '10' },
];

const brushSizeCustomStyles = {
  option: (provided, _state) => ({
    ...provided,
    padding: '5px 7px',
    margin: 0,
  }),
  control: (_provided, _state) => ({
    // ...provided,
    width: 50,
    height: 20,
    padding: 0,
    margin: 0,
  }),
  placeholder: (provided, _state) => ({
    ...provided,
    padding: 0,
    marginLeft: '-4px',
    marginTop: '-2px',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 100,
    marginLeft: '4px',
  }),
  input: () => ({
    color: 'transparent',
  }),
  singleValue: (provided, _state) => ({
    ...provided,
    padding: 0,
    marginLeft: '4px',
    marginTop: '-2px',
  })
};

const shapeCustomStyles = {
  option: (provided, _state) => ({
    ...provided,
    padding: '4px 0',
    margin: '0 -2px',
  }),
  control: (_provided, _state) => ({
    // ...provided,
    height: 20,
    padding: 0,
    margin: 0,
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 100,
  }),
  input: () => ({
    color: 'transparent',
  }),
  placeholder: (provided, _state) => ({
    ...provided,
    padding: 0,
    marginLeft: '4px',
    marginTop: '-2px',
  }),
};

const lineCustomStyles = {
  option: (provided, _state) => ({
    ...provided,
    padding: '0px 0px',
    margin: '0px 8px',
  }),
  control: (_provided, _state) => ({
    // ...provided,
    height: 20,
    width: 70,
    padding: 0,
    margin: 0,
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 100,
    marginLeft: '8px',
  }),
  input: () => ({
    color: 'transparent',
  }),
  placeholder: (provided, _state) => ({
    ...provided,
    padding: 0,
    marginLeft: '4px',
    marginTop: '-2px',
  }),
};

const pointCustomStyles = {
  option: (provided, _state) => ({
    ...provided,
    padding: '4px 0',
    margin: 0,
  }),
  control: () => ({
    // none of react-select's styles are passed to <Control />
    height: 20,
    width: 60,
    padding: 0,
    margin: 0,
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 100,
    marginLeft: '12px',
  }),
  input: () => ({
    color: 'transparent',
  }),
  placeholder: (provided, _state) => ({
    ...provided,
    padding: 0,
    marginLeft: '4px',
    marginTop: '-2px',
  }),
};

const brushSizePlaceholder = <div className='brushSizeToolButton drawToolbarButton' />;
const shapePlaceholder = <div className='shapeToolButton drawToolbarButton' />;
const linePlaceholder = <div className='lineToolButton drawToolbarButton' />;
const pointPlaceholder = <div className='pointToolButton drawToolbarButton' />;

const Draw = observer(({ lessonElementId }) => {
  const {
    drawManager,
    lessonManager,
    responseManager,
  } = useContext(MobXProviderContext);

  const [showSubmissionWarning, setShowSubmissionWarning] = useState(false);
  const [showPreviousResponseConfirm, setShowPreviousResponseConfirm] = useState(false);
  const [showPreviewUnavailable, setShowPreviewUnavailable] = useState(false);

  const model = lessonManager.getSlideModel(lessonElementId);
  const drawService = [];
  drawService[lessonElementId] = useMemo(() => new DrawService(model), [lessonElementId]); // eslint-disable-line react-hooks/exhaustive-deps
  drawManager.init(lessonElementId);

  const [rerender, setRerender] = useState(false);

  const imageLoaded = (e) => {
    const { clientHeight } = e.target;
    const bodyHeight = drawService[lessonElementId].getHeight();

    const finalHeight = ((bodyHeight - clientHeight) / 2);
    e.target.style.marginTop = (lessonManager.playerMode === LessonMode.PRINT_PREVIEW) ? '0' : `${finalHeight}px`;
  };

  useStyleEvents(lessonElementId);

  // Get lesson element state early so it can be used in useEffect
  const lessonElementState = responseManager.getLessonElementState(lessonElementId);
  const readOnly = lessonElementState ? (lessonElementState.readOnly || lessonManager.isReadOnly) : false;


  useEffect(() => {
    const init = async () => {
      // remove all text containers leftover from other Draw slides
      // await drawManager.initializeTextContainers([], lessonElementId);

      await drawService[lessonElementId].initialize();
      await drawService[lessonElementId].runtimeObjectReady();

      await drawService[lessonElementId].showResponse();
      // possible timing issue with text containers from other Draw slides showing - rerender may fix it
      setRerender(!rerender);

      if (!UtilsService.isNullOrEmpty(model.imageSource) && UtilsService.isNullOrEmpty(model.altText) && !model.alTextLoaded && UtilsService.isNullOrEmpty(altText)) {
        const contentItemId = model.imageSource.substring(0, model.imageSource.indexOf('.'));
        const contentItem = await lessonService.fetchContentItem(contentItemId);
        if (contentItem && contentItem.altText) {
          setAltText(contentItem.altText);
          model.altText = contentItem.altText;
          model.alTextLoaded = true;
        } else {
          if (!altText) {
            if (imageTitle) {
              setAltText(UtilsService.stripTagsAndEntities(model.imageTitle));
              model.altText = UtilsService.stripTagsAndEntities(model.imageTitle);
              model.alTextLoaded = true;
            }
          }
        }
      }
    };
    if (lessonManager.playerMode !== LessonMode.PRINT_PREVIEW) {
      init();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for response changes and re-render the canvas
  useEffect(() => {
    if (lessonElementState && lessonElementState.currentResponseAnswer && drawService[lessonElementId]) {
      // Small delay to ensure the response data is fully updated
      const timer = setTimeout(() => {
        drawService[lessonElementId].showResponse();
        setRerender(!rerender);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [lessonElementState?.currentResponseAnswer, lessonElementId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for read-only state changes and deactivate drawing mode when read-only
  useEffect(() => {
    if (drawService[lessonElementId] && readOnly) {
      // Deactivate drawing mode when question becomes read-only
      drawService[lessonElementId].setDrawingMode(false);
      // Deselect any active tools
      drawService[lessonElementId].deselectToolbarButtons();
    }
  }, [readOnly, lessonElementId]); // eslint-disable-line react-hooks/exhaustive-deps

  // let imgSrc = `${Auth.ecms}/api/redirectToResource?authKey=${Auth.authKey}&resourceFolder=userFiles&resourceFileName=${model.imageSource}`;

  const strokeColor = drawService[lessonElementId].currentStrokeColor;
  const fillColor = drawService[lessonElementId].currentFillColor;

  const width = drawService[lessonElementId].getWidth();
  const height = drawService[lessonElementId].getHeight();

  let widthPx = `${width }px`;
  let heightPx = `${height }px`;

  const imageTitle = model.imageTitle || '';
  // const imageTitleId = drawService[lessonElementId].getImageTitleId();
  const [altText, setAltText] = useState(model && model.altText ? model.altText : '');

  const canvasId = `${model.id }_canvas`;
  const drawCanvasId = `${model.id }_drawCanvas`;

  let imageSrc = null;
  if (model.croppedImageSource) {
    imageSrc = Auth.getResourceUrlByFileName(model.croppedImageSource);
  } else if (model.imageSource) {
    imageSrc = Auth.getResourceUrlByFileName(model.imageSource);
  }

  if (lessonManager.playerMode === LessonMode.PRINT_PREVIEW) {
    widthPx = (imageSrc) ? '3in' : '1.5in';
    heightPx = (imageSrc) ? '3in' : '1.5in';
  }

  let toolbar = MINIMUM_TOOLBAR;
  if (model.responseFormat === 'html') {
    if (model.toolbarType === 'mathToolbar') {
      toolbar = MATH_TOOLBAR;
    } else if (model.toolbarType === 'advancedToolbar') {
      toolbar = ADVANCED_CKEDITOR_TOOLBAR;
    } else if (model.toolbarType === 'mathAdvancedToolbar') {
      toolbar = ADVANCED_MATH_CKEDITOR_TOOLBAR;
    } else if (model.toolbarType === 'mathWithTableToolbar') {
      toolbar = MATH_WITH_TABLE_TOOLBAR;
    }
  }
  // richText.createEditor(textId, {onClick:function() {
  //     self.disableTextDragging();
  //     richText.getEditor(textId).focus();
  //   },
  //   onBlur:function(e) {
  //     self.updateState();
  //     self.enableTextDragging();
  //   },
  //   toolbar:toolbar
  // });

  const shapeOptions = [
    { value: drawService[lessonElementId].TRIANGLE, label: <span className={`shapeIcon shapeIcon-${drawService[lessonElementId].TRIANGLE}`}>&nbsp;</span> },
    { value: drawService[lessonElementId].SQUARE, label: <span className={`shapeIcon shapeIcon-${drawService[lessonElementId].SQUARE}`}>&nbsp;</span> },
    { value: drawService[lessonElementId].CIRCLE, label: <span className={`shapeIcon shapeIcon-${drawService[lessonElementId].CIRCLE}`}>&nbsp;</span> },
    { value: drawService[lessonElementId].STAR, label: <span className={`shapeIcon shapeIcon-${drawService[lessonElementId].STAR}`}>&nbsp;</span> },
  ];

  const pointOptions = [
    { value: 'closed', label: <span className='pointIcon pointIcon-closed'>&nbsp;</span> },
    { value: 'open', label: <span className='pointIcon pointIcon-open'>&nbsp;</span> },
  ];

  const lineOptions = [
    { value: drawService[lessonElementId].LINE, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].LINE}`}>&nbsp;</span> },
    { value: `${drawService[lessonElementId].LINE}-dashed`, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].LINE}-dashed`}>&nbsp;</span> },
    { value: drawService[lessonElementId].RAY, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].RAY}`}>&nbsp;</span> },
    { value: `${drawService[lessonElementId].RAY}-dashed`, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].RAY}-dashed`}>&nbsp;</span> },
    { value: drawService[lessonElementId].SEGMENT, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].SEGMENT}`}>&nbsp;</span> },
    { value: `${drawService[lessonElementId].SEGMENT}-hidden-hidden`, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].SEGMENT}-hidden-hidden`}>&nbsp;</span> },
    { value: `${drawService[lessonElementId].SEGMENT}-dashed`, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].SEGMENT}-dashed`}>&nbsp;</span> },
    { value: drawService[lessonElementId].VECTOR, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].VECTOR}`}>&nbsp;</span> },
    { value: `${drawService[lessonElementId].VECTOR}-open-hidden`, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].VECTOR}-open-hidden`}>&nbsp;</span> },
    { value: `${drawService[lessonElementId].VECTOR}-dashed`, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].VECTOR}-dashed`}>&nbsp;</span> },
  ];

  const handleChangeStrokeColor = (color) => {
    drawService[lessonElementId].setStrokeColor(color.hex);
  };

  const handleChangeFillColor = (color) => {
    drawService[lessonElementId].setFillColor(color.hex);
  };

  const handleChangeBrushSize = (data) => {
    ReactTooltip.hide();
    drawService[lessonElementId].setStrokeWidth(data.value);
  };

  const handleChangeShape = (data) => {
    ReactTooltip.hide();
    drawService[lessonElementId].addShape(data.value);
  };

  const handleChangeLine = (data) => {
    ReactTooltip.hide();
    drawService[lessonElementId].addLine(data.value);
  };

  const handleChangePoint = (data) => {
    ReactTooltip.hide();
    drawService[lessonElementId].addPoint(data.value);
  };

  const handleSelect = () => {
    drawService[lessonElementId].select();
  };

  const handleCopy = () => {
    drawService[lessonElementId].copy();
  };

  const handleUndo = () => {
    drawService[lessonElementId].undo();
  };

  const handleRedo = () => {
    drawService[lessonElementId].redo();
  };

  const handleDeleteText = (textId, lessonElementId) => {
    drawManager.deleteTextContainer(textId, lessonElementId);
    drawService[lessonElementId].updateState();
    drawService[lessonElementId].updateResponseModel();
  };

  const navToCurrentLessonElement = async () => {
    if (lessonManager.currentLessonElementId !== lessonElementId) {
      responseService.setSolutionDialogOpen(false);
      responseService.setHintDialogOpen(false);
      await navMenuService.navToSlideClickHandler(lessonElementId, { shouldScrollQuestionIntoView: false });
    }
  };

  const handleDragStart = (e) => {
    navToCurrentLessonElement();
    const rect = document.getElementById(e.target.id).getBoundingClientRect();
    drawManager.dragStartTextContainer(e.target.id, e.clientX, e.clientY, rect.width, rect.height, lessonElementId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = (e) => {
    e.preventDefault();
    drawManager.dropTextContainer(e.clientX, e.clientY, lessonElementId);
    drawService[lessonElementId].updateState();
    drawService[lessonElementId].updateResponseModel();
  };

  const textContainers = drawManager.textContainers[lessonElementId];

  // Check if this question has response dependency
  const responseDependencyValue = lessonManager.lessonOptions?.responseDependency?.[lessonElementId];
  let hasResponseDependency = false;
  let isDifferentLessonSource = false;
 
  if (responseDependencyValue) {
    // Handle new format where value is an object
    if (typeof responseDependencyValue === 'object') {
      if (responseDependencyValue.source === 'inLesson') {
        hasResponseDependency = responseDependencyValue.questionId;
      } else if (responseDependencyValue.source === 'differentLesson') {
        isDifferentLessonSource = true;
        hasResponseDependency = responseDependencyValue.lessonId && 
                                responseDependencyValue.questionForeverId &&
                                responseDependencyValue.lessonId !== '' &&
                                responseDependencyValue.questionForeverId !== '';
      }
    } else {
      // Handle old format where value is a string (questionId)
      hasResponseDependency = responseDependencyValue !== null && responseDependencyValue !== '';
    }
  }
  const isSubmitted = lessonElementState ? lessonElementState.isSubmitted : false;

  // Handler for showing previous response
  const handleShowPreviousResponse = async () => {
    if (lessonElementState && lessonElementState.questionBehavior && lessonElementState.questionBehavior.hasResponseDependency(model, lessonElementId)) {
      // Check if this is a differentLesson source
      if (isDifferentLessonSource) {
        // Check if we're in activity mode
        if (PlayerService.isActivityMode()) {
          // Call the API to fetch prior responses
          await lessonElementState.questionBehavior.fetchPriorResponsesFromDifferentLesson(responseDependencyValue, lessonElementId);
        } else {
          // Show dialog for non-activity mode
          setShowPreviewUnavailable(true);
        }
        return;
      }
      
      // Check if user has current content that would be overwritten
      if (lessonElementState.questionBehavior.hasCurrentContent(lessonElementId)) {
        setShowPreviousResponseConfirm(true);
      } else {
        // No current content, proceed directly
        lessonElementState.questionBehavior.populateWithPreviousResponse(lessonElementId, model);
      }
    }
  };

  const handleConfirmShowPreviousResponse = () => {
    setShowPreviousResponseConfirm(false);
    if (lessonElementState && lessonElementState.questionBehavior && lessonElementState.questionBehavior.hasResponseDependency(model, lessonElementId)) {
      lessonElementState.questionBehavior.populateWithPreviousResponse(lessonElementId, model);
    }
  };

  const handleCancelShowPreviousResponse = () => {
    setShowPreviousResponseConfirm(false);
  };

  // Handler for submission with warning
  const handleSubmitWithWarning = () => {
    if (hasResponseDependency && !isSubmitted) {
      setShowSubmissionWarning(true);
    } else {
      // Proceed with normal submission
      responseService.submitResponse(lessonElementId);
    }
  };

  const handleConfirmSubmission = () => {
    setShowSubmissionWarning(false);
    responseService.submitResponse(lessonElementId);
  };

  const handleCancelSubmission = () => {
    setShowSubmissionWarning(false);
  };

  return (
    <div>
      <div className='testItemQuestion'>
        {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && <PrintQuestionNumber model={model} />}
        <HtmlComponent htmlStr={model.questionText} />
      </div>
      <HtmlComponent className='image-title' htmlStr={model.imageTitle} />

      {/* Show Previous Response Button */}
      {hasResponseDependency && lessonManager.playerMode !== LessonMode.SCORING && lessonManager.playerMode !== LessonMode.REVIEW && (
        <div className='response-dependency-controls'>
          <ShowPreviousResponseButton
            lessonElementId={lessonElementId}
            onShowPreviousResponse={handleShowPreviousResponse}
            isDisabled={isSubmitted}
          />
        </div>
      )}

      <>
        {(lessonManager.playerMode !== LessonMode.PRINT_PREVIEW) && (
          <div className='drawToolbar' role='toolbar' style={{ width: widthPx }}>

            <ReactTooltip border effect='solid' id='draw' padding='10px'
              type='light' />

            <div aria-label={t2('drawwithpencil')} className='pencilToolButton drawToolbarButton' data-for='draw' data-tip={t2('pencil\n')}
              data-tool='pencil' onClick={() => !readOnly && drawService[lessonElementId].onClickPencilTool()} role='button'
              tabIndex='0' style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }}
            />

            <div aria-label={t2('drawwithpaintbrush')} className='paintToolButton drawToolbarButton' data-for='draw' data-tip={t2('brush')}
              data-tool='paint' onClick={() => !readOnly && drawService[lessonElementId].onClickPaintTool()} role='button'
              tabIndex='0' style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }}
            />

            <div aria-label={t2('strokecolor')} className='strokeColorButton drawToolbarButton' data-for='draw' data-tip={t2('strokecolor')}
              role='menu' style={{ display: 'inline-block', opacity: readOnly ? 0.5 : 1 }} tabIndex='0'>
              <ColorPicker color={strokeColor} onChange={readOnly ? () => {} : handleChangeStrokeColor} disabled={readOnly} />
            </div>

            <div aria-label={t2('fillercolor')} className='fillColorButton drawToolbarButton' data-for='draw' data-tip={t2('fillercolor')}
              role='menu' style={{ display: 'inline-block', opacity: readOnly ? 0.5 : 1 }} tabIndex='0'>
              <ColorPicker color={fillColor} onChange={readOnly ? () => {} : handleChangeFillColor} disabled={readOnly} />
            </div>

            <div className='drawBrushSizeMenuContainer' style={{ display: 'inline-block' }} tabIndex='-1'>
              <div aria-label={t2('brushsize')} className='drawBrushSizeMenu drawToolMenu' data-for='draw' data-tip={t2('brushsize')}
                role='menu' style={{ width: '40px' }} tabIndex='-0'>
                <Select
                  components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                  onChange={readOnly ? () => {} : handleChangeBrushSize}
                  options={brushSizeOptions}
                  placeholder={brushSizePlaceholder}
                  styles={brushSizeCustomStyles}
                  isDisabled={readOnly}
                />
              </div>
            </div>

            <div className='shapeMenuContainer' style={{ display: 'inline-block' }} tabIndex='-1'>
              <div aria-label={t2('shape')} className='shapeMenu drawToolMenu' data-for='draw' data-tip={t2('shape')}
                role='menu' style={{ width: '50px' }} tabIndex='0'>
                <Select
                  components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null, ValueContainer: CustomValueContainer }}
                  onChange={readOnly ? () => {} : handleChangeShape}
                  options={shapeOptions}
                  placeholder={shapePlaceholder}
                  styles={shapeCustomStyles}
                  isDisabled={readOnly}
                />
              </div>
            </div>

            <div className='lineMenuContainer' style={{ width: '60px', display: 'inline-block' }} tabIndex='-1'>
              <div aria-label={t2('graphline')} className='lineMenu drawToolMenu' data-for='draw' data-tip={t2('graphline')}
                role='menu' style={{ width: '60px' }} tabIndex='0'>
                <Select
                  components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null, ValueContainer: CustomValueContainer }}
                  onChange={readOnly ? () => {} : handleChangeLine}
                  options={lineOptions}
                  placeholder={linePlaceholder}
                  styles={lineCustomStyles}
                  isDisabled={readOnly}
                />
              </div>
            </div>

            <div className='pointMenuContainer' style={{ display: 'inline-block' }} tabIndex='-1'>
              <div aria-label={t2('graphpoint')} className='pointMenu drawToolMenu' data-for='draw' data-tip={t2('graphpoint')}
                role='menu' style={{ width: '50px' }} tabIndex='0'>
                <Select
                  components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null, ValueContainer: CustomValueContainer }}
                  onChange={readOnly ? () => {} : handleChangePoint}
                  options={pointOptions}
                  placeholder={pointPlaceholder}
                  styles={pointCustomStyles}
                  isDisabled={readOnly}
                />
              </div>
            </div>

            <div aria-label={t2('selectitems')} className='selectToolButton drawToolbarButton' data-for='draw' data-tip={t2('selectitems')}
              onClick={() => !readOnly && handleSelect()} role='button' tabIndex='0' 
              style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }} />

            <div aria-label={t2('addtext')} className='textToolButton drawToolbarButton' data-for='draw' data-tip={t2('addtext')}
              onClick={() => !readOnly && drawService[lessonElementId].onClickTextTool()} role='button' tabIndex='0' 
              style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }} />

            <div className='rightButtonContainer' tabIndex='-1'>
              <div aria-label={t2('copyelement')} className='drawCopyElementButton drawToolbarButton' data-for='draw' data-tip={t2('copyelement')}
                onClick={() => !readOnly && handleCopy()} role='button' tabIndex='0' 
                style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }} />
              <div aria-label={t2('undo')} className='drawUndoButton drawToolbarButton' data-for='draw' data-tip={t2('undo')}
                onClick={() => !readOnly && handleUndo()} role='button' tabIndex='0' 
                style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }} />
              <div aria-label={t2('redo')} className='drawRedoButton drawToolbarButton' data-for='draw' data-tip={t2('redo')}
                onClick={() => !readOnly && handleRedo()} role='button' tabIndex='0' 
                style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }} />
              <div aria-label={t2('deleteelement')} className='drawDeleteElementButton drawToolbarButton' data-for='draw' data-tip={t2('deleteelement')}
                onClick={() => !readOnly && drawService[lessonElementId].deleteFromDraw()} role='button' tabIndex='0' 
                style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }} />
            </div>

            <div className='deleteButtonContainer'>
              <button className='deleteElementButton' disabled={readOnly} style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }}>{t('delete')}</button>
              <button className='clearButton' disabled={readOnly} style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }}>{t('clearAll')}</button>
            </div>
          </div>
        )}
        <div id={`${model.id}-draw-wrapper`} className='draw-wrapper' style={{ position: 'relative', width: widthPx, height: heightPx }}>
          <div className='drawBody imageBody ' data-allow='1' style={{ width: widthPx, height: heightPx }}>
            {imageSrc && (
              <img alt={altText} className='imageLabelImage' onLoad={imageLoaded} src={imageSrc}
                style={{ maxHeight: heightPx }} tabIndex={0} />
            )}
          </div>
          {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && (
            <div className='canvasContainerOuter' id='canvasContainerOuter' onDragEnd={handleDragEnd}
              style={{ height: heightPx, width: widthPx }} />
          )}
          {(lessonManager.playerMode !== LessonMode.PRINT_PREVIEW) && (
            <div className='canvasContainerOuter' id='canvasContainerOuter' onDragEnd={handleDragEnd}
              style={{ height: heightPx, width: widthPx }}>

              {textContainers.map((textContainer) => {
                return (
                  <div key={textContainer.textId} className='drawTextContainer deleteButtonParent' draggable={!readOnly} id={textContainer.textId}
                    onDragStart={readOnly ? () => {} : handleDragStart} style={{ top: textContainer.top, left: textContainer.left, opacity: textContainer.opacity }}>
                    <div className='deleteButton deleteTextContainerButton'
                      onClick={() => !readOnly && handleDeleteText(textContainer.textId, lessonElementId)} 
                      style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }} />
                    <div className='drawText oneRowToolbar cke_editable cke_editable_inline cke_contents_ltr cke_show_borders placeholder' id={`${textContainer.textId}_text`}>

                      <RichTextEditor
                        data={(textContainer.text) ? textContainer.text : ''}
                        lessonElementId={lessonElementId}
                        onBlur={() => {
                          if (!readOnly) {
                            drawService[lessonElementId].updateState();
                            drawService[lessonElementId].updateResponseModel();
                          }
                        }}
                        onChange={(data) => {
                          if (!readOnly) {
                            drawManager.setTextContainerText(textContainer.textId, data, lessonElementId);
                          }
                        }}
                        placeHolder={t('enterText')}
                        readOnly={readOnly}
                        toolbar={toolbar} />
                    </div>
                  </div>
                );
              })}

              <div className='body canvasOrganizerBody  canvasContainer' id={`${model.id}_canvasContainer`} style={{ height: heightPx, width: widthPx }}>
                <canvas className='canvas' id={canvasId} />
              </div>

              <div className='body canvasOrganizerBody  drawCanvasContainer' id={`${model.id}_drawCanvasContainer`} style={{ width: widthPx, height: heightPx }}>
                <canvas className='canvas' id={drawCanvasId} />
              </div>

              {/* Invisible overlay to prevent all interactions when read-only */}
              {readOnly && (
                <div 
                  className='draw-readonly-overlay' 
                  style={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    width: '100%', 
                    height: '100%', 
                    zIndex: 10,
                    pointerEvents: 'all',
                    backgroundColor: 'transparent'
                  }} 
                />
              )}

            </div>
          )}
        </div>
      </>
      {((lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && lessonManager.isForceTeacher) && (
        <div className='print-teacher-notes'>
          <HtmlComponent htmlStr={model.validation.solution} />
        </div>
      )}

      {/* Submission Warning Dialog */}
      <SubmissionWarningDialog
        isOpen={showSubmissionWarning}
        onConfirm={handleConfirmSubmission}
        onCancel={handleCancelSubmission}
        message={t('submissionWarningMessage')}
      />

      {/* Show Previous Response Confirmation Dialog */}
      <ShowPreviousResponseConfirmDialog
        isOpen={showPreviousResponseConfirm}
        onConfirm={handleConfirmShowPreviousResponse}
        onCancel={handleCancelShowPreviousResponse}
        message={t('showPreviousResponseWarning')}
      />
      
      {/* Preview Unavailable Dialog */}
      <PreviewUnavailableDialog
        isOpen={showPreviewUnavailable}
        onClose={() => setShowPreviewUnavailable(false)}
        message="This feature is only available during live question answering sessions. It cannot be previewed in this mode."
      />

    </div>
  );
});

export default Draw;
